import axios from 'axios';
import config from '../config/config';
import globals from '../services/globals';
import { clearStoragePreservingSeen } from '../utils/persistentStorage';

const client = axios.create({
  timeout: 30000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

// Attach auth headers if available
client.interceptors.request.use((request) => {
  try {
    const currentUser = globals().getLogedInUser?.();
    if (currentUser) {
      const userId = currentUser?.data?.id || 0;
      const token = currentUser?.token || '';
      request.headers = {
        ...(request.headers || {}),
        userid: String(userId),
        Authorization: token ? `Bearer ${token}` : '',
      };
    }
  } catch (e) {
    // no-op; avoid breaking requests on header prep
  }
  return request;
});

// Auto-refresh token on 401
let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
}

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error?.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        // Queue this request until refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return client(originalRequest);
        });
      }

      isRefreshing = true;
      const refreshToken = sessionStorage.getItem('refreshToken') || localStorage.getItem('refreshToken');

      if (!refreshToken) {
        isRefreshing = false;
        processQueue(new Error('No refresh token'), null);
        window.location.replace('/?logout=1');
        return Promise.reject(error);
      }

      try {
        const baseUrl = config?.baseUrl || '';
        const res = await axios.post(`${baseUrl}api/auth/refresh`, { refreshToken });

        if (res.data?.success && res.data?.data?.token) {
          const newToken = res.data.data.token;
          const newRefresh = res.data.data.refreshToken;

          // Update stored user token
          const raw = sessionStorage.getItem('logedInUser');
          if (raw) {
            try {
              const user = JSON.parse(raw);
              user.token = newToken;
              sessionStorage.setItem('logedInUser', JSON.stringify(user));
            } catch {}
          }
          const cookie = document.cookie.split(';').find(c => c.trim().startsWith('logedInUser='));
          if (cookie) {
            try {
              const val = decodeURIComponent(cookie.split('=').slice(1).join('='));
              const user = JSON.parse(val);
              user.token = newToken;
              const d = new Date();
              d.setTime(d.getTime() + 4 * 24 * 60 * 60 * 1000);
              document.cookie = `logedInUser=${encodeURIComponent(JSON.stringify(user))};expires=${d.toUTCString()};path=/`;
            } catch {}
          }
          if (newRefresh) {
            sessionStorage.setItem('refreshToken', newRefresh);
            localStorage.setItem('refreshToken', newRefresh);
          }

          isRefreshing = false;
          processQueue(null, newToken);

          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return client(originalRequest);
        }
        // Refresh responded but without a valid token — treat as a failed
        // refresh so queued requests are settled instead of hanging forever.
        isRefreshing = false;
        processQueue(error, null);
        try { sessionStorage.clear(); } catch {}
        clearStoragePreservingSeen();
        window.location.replace('/?logout=1');
        return Promise.reject(error);
      } catch (refreshError) {
        isRefreshing = false;
        processQueue(refreshError, null);
        // Vetëm një refuzim i qartë i token-it e mbyll sesionin. Një 429 (kufiri
        // i kërkesave), një 500 ose një rrjet i rënë janë kalimtare — dhe deri
        // tani e nxirrnin njeriun jashtë në mes të punës, pa pyetur.
        const st = refreshError?.response?.status;
        if (st === 401 || st === 403) {
          try { sessionStorage.clear(); } catch { /* noop */ }
          clearStoragePreservingSeen();
          window.location.replace('/?logout=1');
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// Helper to build feature base URL (db-aware)
export const makeBaseUrl = (dbName) => {
  try {
    const currentUser = globals().getLogedInUser?.();
    const company = dbName || currentUser?.company || config?.company || '';
    return `${config?.baseUrl || ''}${company}/mapi`;
  } catch (e) {
    const fallbackCompany = dbName || config?.company || '';
    return `${config?.baseUrl || ''}${fallbackCompany}/mapi`;
  }
};

export default client;
